import { useEffect, useState } from "react";
import api, { formatUSD } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save, GripVertical } from "lucide-react";
import { Input } from "@/components/ui/input";

export default function AdminSides() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/sides");
      setItems(data.sides || []);
    } catch { toast.error("Loading failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const add = () => setItems((s) => [...s, { name: "", price: 0 }]);
  const update = (i, field, value) => setItems((s) => s.map((it, idx) => idx === i ? { ...it, [field]: field === "price" ? value : value } : it));
  const remove = (i) => setItems((s) => s.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { items: items.filter((it) => (it.name || "").trim()).map((it) => ({ name: it.name.trim(), price: parseFloat(it.price) || 0 })) };
      await api.put("/admin/sides", payload);
      toast.success("Sides catalog saved");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="admin-sides-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-[var(--dd-green)]">Side dishes catalog</h1>
        <p className="text-sm text-[var(--dsf-text-secondary)] mt-1 max-w-2xl">
          Global list of side options shown on every dish that supports sides.
          Set a <strong>$0.00 price</strong> to make a side free. Customers also get a "No side" option automatically.
        </p>
      </div>

      <div className="bg-white rounded-2xl border border-[var(--dsf-border)] p-4 md:p-6">
        {loading ? (
          <div className="py-8 text-center text-[var(--dsf-text-secondary)]">Loading…</div>
        ) : (
          <div className="space-y-3">
            <div className="hidden md:grid grid-cols-12 gap-3 text-[10px] uppercase tracking-[0.2em] text-[var(--dsf-text-muted)] font-bold pb-2 border-b border-[var(--dsf-border)]">
              <div className="col-span-7">Side name</div>
              <div className="col-span-3">Price (USD)</div>
              <div className="col-span-2 text-right">Actions</div>
            </div>
            {items.length === 0 && (
              <div className="py-8 text-center text-[var(--dsf-text-secondary)]" data-testid="sides-empty">
                No sides configured yet. Click "Add side" below.
              </div>
            )}
            {items.map((it, i) => (
              <div key={i} className="grid grid-cols-12 gap-3 items-center" data-testid={`side-row-${i}`}>
                <div className="col-span-12 md:col-span-7 flex items-center gap-2">
                  <GripVertical size={14} className="text-[var(--dsf-text-muted)] shrink-0" />
                  <Input
                    value={it.name}
                    onChange={(e) => update(i, "name", e.target.value)}
                    placeholder="e.g. White rice"
                    data-testid={`side-name-${i}`}
                  />
                </div>
                <div className="col-span-9 md:col-span-3">
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[var(--dsf-text-muted)]">$</span>
                    <Input
                      type="number" step="0.01" min="0"
                      value={it.price}
                      onChange={(e) => update(i, "price", e.target.value)}
                      className="pl-7"
                      data-testid={`side-price-${i}`}
                    />
                  </div>
                </div>
                <div className="col-span-3 md:col-span-2 flex justify-end">
                  <button
                    onClick={() => remove(i)}
                    className="text-[var(--dd-coral)] hover:bg-red-50 rounded-full w-9 h-9 flex items-center justify-center transition"
                    data-testid={`side-remove-${i}`}
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
                {parseFloat(it.price) === 0 && it.name && (
                  <div className="col-span-12 md:col-start-8 md:col-span-3 -mt-1.5">
                    <span className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">FREE</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between mt-6 pt-6 border-t border-[var(--dsf-border)] gap-3 flex-wrap">
          <button
            onClick={add}
            className="text-sm flex items-center gap-2 px-4 py-2 rounded-full border border-dashed border-[var(--dd-green)] text-[var(--dd-green)] hover:bg-[var(--dd-green-soft)]"
            data-testid="sides-add"
          >
            <Plus size={14} /> Add side
          </button>
          <button
            onClick={save} disabled={saving}
            className="btn-primary disabled:opacity-50"
            data-testid="sides-save"
          >
            <Save size={14} /> {saving ? "Saving…" : "Save catalog"}
          </button>
        </div>
      </div>
    </div>
  );
}
